import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import Question from '@monofront/design-system/visuals/Icons/Old/Question';
import {
  Input,
  Label,
  TextInput,
  ErrorText,
} from '@monofront/design-system/components/FormV2';
import Popover from '@components/Popover';
import { colors, fontWeight } from '@monofront/theme';
import { useTranslations } from 'next-intl';
import { Frames, CardNumber, ExpiryDate, Cvv } from '@monofront/frames-react';
import getConfig from 'next/config';
import isProductionMode from '@utils/isProductionMode';
import { toast } from 'react-toastify';
import logger from '@utils/logger';
import FormContext from '@components/PaymentForms/FormContext';
import { useCheckoutComMachine } from '@monofront/business/contexts';
import { CHECKOUT_COM_CARD_FORM_STATES } from '@monofront/business/stateMachines';
import classNames from 'classnames';

interface ICheckoutComCardFormProps {
  isLoading?: boolean;
  onComplete?: (isValid: boolean) => void;
  children: React.ReactNode;
  onSubmit?: (formData: any) => void;
  fullWidth?: boolean;
}

const { publicRuntimeConfig } = getConfig();

const CheckoutComCardForm: React.FC<ICheckoutComCardFormProps> = ({
  children,
  isLoading,
  onComplete,
  onSubmit,
  fullWidth,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
    watch,
    formState: { dirtyFields },
  } = useForm({
    mode: 'onChange',
    criteriaMode: 'all',
  });
  const [isCardFormCompleted, setIsCardFormCompleted] = useState(false);
  const { interpreter } = useCheckoutComMachine();

  const holderName = watch('holderName', '');
  const tAccount = useTranslations('Account');
  const tGlobal = useTranslations('Global');
  const tCard = useTranslations('Card');

  const errorCards = {
    'expiry-date': tCard('invalidExpiryDate'),
    cvv: tCard('incompleteCVC'),
    'card-number': tCard('invalidCardNumber'),
  };

  useEffect(() => {
    if (
      isCardFormCompleted &&
      'holderName' in dirtyFields &&
      !errors.holderName
    ) {
      onComplete?.(true);

      return;
    }

    onComplete?.(false);
  }, [dirtyFields, holderName, errors, isCardFormCompleted]);

  // enable re-submit form after a reset
  useEffect(() => {
    const handler = async (state) => {
      if (state.value === CHECKOUT_COM_CARD_FORM_STATES.INITIAL) {
        Frames.enableSubmitForm();
      }
    };

    interpreter?.onTransition(handler);

    return () => {
      interpreter?.off(handler);
    };
  }, [interpreter]);

  return (
    <FormContext.Provider
      value={useMemo(
        () => ({ clearErrors, errors, isLoading, register, setError }),
        [clearErrors, errors, isLoading, register, setError],
      )}
    >
      <Frames
        config={{
          debug: !isProductionMode,
          publicKey: publicRuntimeConfig.checkoutComPublicKey,
          localization: {
            cardNumberPlaceholder: tCard('cardNumber'),
            expiryMonthPlaceholder: tCard('expiryMonthPlaceholder'),
            expiryYearPlaceholder: tCard('expiryYearPlaceholder'),
            cvvPlaceholder: tCard('cardCVCPlaceholder'),
          },
          cardholder: {
            name: holderName,
          },
          style: {
            base: {
              fontSize: '16px',
              fontFamily: 'sans-serif',
              fontWeight: fontWeight.light,
              color: colors.black,
              letterSpacing: 'initial',
            },
            invalid: {
              color: colors.danger,
            },
            placeholder: {
              base: {
                color: colors.grey1,
                fontFamily: 'sans-serif',
              },
            },
          },
        }}
        cardValidationChanged={(e) => setIsCardFormCompleted(e.isValid)}
        frameValidationChanged={(e) => {
          if (!e.isValid) {
            setError(e.element, {
              type: 'cardError',
              message: errorCards[e.element],
            });
          } else {
            clearErrors(e.element);
          }
        }}
        cardTokenizationFailed={(e) => {
          logger.error('Card tokenization failed', {
            error: e,
          });
          toast(tGlobal('errorOccurredTryAgain'), {
            progressClassName: 'toast-error',
          });
        }}
      >
        <div className="tw-mb-4 tw-flex tw-w-full tw-justify-center [&_label]:tw-mb-1 [&_label]:tw-font-bold">
          <form
            className={classNames(
              `tw-mt-2 tw-grid tw-w-full tw-max-w-[380px] tw-grid-cols-2 tw-gap-4`,
              {
                'tw-max-w-initial': fullWidth,
              },
            )}
            onSubmit={onSubmit && handleSubmit(onSubmit)}
          >
            <TextInput
              id="name"
              isInvalid={Boolean(errors.holderName)}
              UNSAFE_className="tw-col-span-2"
            >
              <Label>{tCard('cardOwner')}*</Label>
              <Input
                type="text"
                placeholder={tAccount('creditCardHolderName')}
                {...register('holderName', {
                  required: {
                    value: true,
                    message: tAccount('mandatoryField'),
                  },
                })}
                dataTestId="CardForm-Input-2"
              />
              {errors.holderName && (
                <ErrorText>{errors.holderName.message as string}</ErrorText>
              )}
            </TextInput>
            <div
              className={`tw-col-span-2 ${
                errors['card-number']
                  ? '[&_.card-number-frame]:tw-lf-input-invalid'
                  : ''
              } [&_.card-number-frame]:tw-lf-input`}
            >
              <TextInput
                id="card-number"
                isInvalid={Boolean(errors['card-number'])}
              >
                <Label>{tCard('cardNumber')}*</Label>
                <CardNumber />
                {errors['card-number'] && (
                  <ErrorText>
                    {errors['card-number']?.message as string}
                  </ErrorText>
                )}
              </TextInput>
            </div>
            <div
              className={`card-item ${
                errors['expiry-date']
                  ? '[&_.expiry-date-frame]:tw-lf-input-invalid'
                  : ''
              } [&_.expiry-date-frame]:tw-lf-input`}
            >
              <TextInput
                id="expiry-date"
                isInvalid={Boolean(errors['expiry-date'])}
              >
                <Label>{tCard('expirationDate')}*</Label>
                <ExpiryDate />
                {errors['expiry-date'] && (
                  <ErrorText>
                    {errors['expiry-date'].message as string}
                  </ErrorText>
                )}
              </TextInput>
            </div>
            <div
              className={`card-item ${
                errors['cvv'] ? '[&_.cvv-frame]:tw-lf-input-invalid' : ''
              } [&_.cvv-frame]:tw-lf-input`}
            >
              <TextInput id="cvv" isInvalid={Boolean(errors['cvv'])}>
                <div className="label-with-popover">
                  <Label>{tCard('cardCVC')}*</Label>
                  <Popover
                    trigger="click"
                    className="popover-container"
                    content={
                      <div className="popover">{tCard('CVCPopOver')}</div>
                    }
                    zIndex={10000}
                  >
                    <button
                      type="button"
                      className="tw-lf-button-clear"
                      aria-label={tCard('whereToFindCVC')}
                      data-testid="NewCardForm-button-clear"
                    >
                      <Question width={14} height={14} />
                    </button>
                  </Popover>
                </div>
                <Cvv />
                {errors['cvv'] && (
                  <ErrorText>{errors['cvv']?.message as string}</ErrorText>
                )}
              </TextInput>
            </div>
            <div className="tw-col-span-2">{children}</div>
          </form>
        </div>

        <style jsx>{`
          div.label-with-popover {
            border: none;
            padding: 0;
            height: initial;
            margin-bottom: var(--space-1);
          }

          .label-with-popover button {
            margin-left: var(--space-1);
            display: inline;
          }

          .label-with-popover :global(label) {
            display: inline;
          }
        `}</style>
      </Frames>
    </FormContext.Provider>
  );
};

export default CheckoutComCardForm;
